import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";

import { Button } from "@/components/atoms/Button";
import { Input } from "@/components/atoms/Input";
import { Rating } from "@/components/atoms/Rating";
import { Textarea } from "@/components/atoms/Textarea";
import FormAddEditReviewSkeleton from "@/components/molecules/AddEditReviewModal/formAddEditReviewSkeleton";
import type { IFormAddEditReviewProps } from "@/components/molecules/AddEditReviewModal/types";
import { useTranslations } from "@/hooks/common/useTranslations";

import { Form, FormField } from "@/providers/formProvider";
import FormControl from "@/providers/formProvider/formControl";
import FormItem from "@/providers/formProvider/formItem";
import FormLabel from "@/providers/formProvider/formLabel";
import FormMessage from "@/providers/formProvider/formMessage";

const FormAddEditReview = ({
  onSubmitFormData,
  isLoadingAddEditReviewModal,
  isLoadingSkeleton,
  onClose,
  review,
}: IFormAddEditReviewProps) => {
  const { t } = useTranslations("SingleAppPage");

  const formSchema = z.object({
    reviewTitle: z.string().min(10, {
      message: t("minCharacter", { characterCount: 10 }),
    }),
    review: z.string().min(15, {
      message: t("minCharacter", { characterCount: 15 }),
    }),
    rate: z
      .number()
      .min(1, { message: t("requiredFields", { fieldName: t("tapToRate") }) }),
  });

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      reviewTitle: "",
      review: "",
      rate: 0,
    },
  });

  useEffect(() => {
    if (review?.id) {
      form.reset({
        reviewTitle: review?.reviewTitle || "",
        review: review?.review || "",
        rate: review?.rate || 0,
      });
    }
  }, [review, form.reset, form]);

  const onSubmit = (values: z.infer<typeof formSchema>) => {
    onSubmitFormData(values);
  };

  return (
    <div className="max-sm:px-4">
      {isLoadingSkeleton ? (
        <FormAddEditReviewSkeleton />
      ) : (
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
            <FormField
              control={form.control}
              name="reviewTitle"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("title")}</FormLabel>
                  <FormControl>
                    <Input placeholder={t("title")} {...field} />
                  </FormControl>

                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="review"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("description")}</FormLabel>
                  <FormControl>
                    <Textarea placeholder={t("review")} rows={7} {...field} />
                  </FormControl>

                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="rate"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("tapToRate")}</FormLabel>
                  <FormControl>
                    <Rating
                      halfFillMode="box"
                      style={{ maxWidth: 140 }}
                      items={5}
                      value={+field.value}
                      onChange={field.onChange}
                    />
                  </FormControl>

                  <FormMessage />
                </FormItem>
              )}
            />
            <div className="flex justify-end max-sm:pb-2">
              <div className="flex gap-4 max-sm:flex-col-reverse max-sm:w-full max-sm:gap-2">
                <Button
                  type="button"
                  variant="outline"
                  className="max-sm:w-full"
                  onClick={onClose}
                >
                  {t("cancel")}
                </Button>
                <Button type="submit" disabled={isLoadingAddEditReviewModal}>
                  {t(review?.id ? "publishChanges" : "publish")}
                </Button>
              </div>
            </div>
          </form>
        </Form>
      )}
    </div>
  );
};

export default FormAddEditReview;
